import { cva, type VariantProps } from 'class-variance-authority'

/**
 * Button variants
 *
 * Variante implementate:
 * - addToCart - Buton "Adaugă în coș" / "Vezi detalii" (bg-button-color)
 * - promo - Butoane promoționale (bg-custom-red)
 * - promoBlock - Buton PromoBlock (bg-black)
 * - newsletter - Newsletter CTA (bg-custom-black cu hover effect)
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded text-sm font-medium ' +
  'ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ' +
  '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:brightness-110',
        destructive: 'bg-destructive text-destructive-foreground hover:brightness-110',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:brightness-110',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        // Add to Cart button - used in ProductCard and product pages
        // gap-0 overrides base gap-2
        // [&_svg]:size-6 for 24px cart icon
        addToCart: 'group/addtocart uppercase tracking-wider bg-button-color text-white font-sec-font font-bold ' +
        'rounded gap-0 [&_svg]:size-6 ' +
        'focus:outline-none focus:bg-button-color active:bg-button-color ' +
        'border border-transparent hover:bg-white hover:text-text-color hover:border-button-color ' +
        'transition-all duration-300 ease-in-out',
        promo: 'bg-custom-red text-white font-bold hover:brightness-110',
        // PromoBlock button - same design as AddToCart (bg-button-color with border hover)
        promoBlock:
          'uppercase tracking-wider bg-button-color text-white font-sec-font text-lg font-bold ' +
          'border border-transparent hover:bg-white hover:text-text-color hover:border-button-color ' +
          'focus:outline-none focus:bg-button-color active:bg-button-color ' +
          'visited:text-white visited:no-underline ' +
          'transition-all duration-300 ease-in-out',
        newsletter: 'uppercase bg-custom-black text-white font-bold hover:bg-main-orange ' +
        'hover:text-custom-black hover:shadow-first-btn-shadow hover:-translate-y-0.5 ' +
        'active:translate-y-0 transition-all duration-300',
        // Scroll to top button - fixed position, circular
        scrollToTop: 'rounded-full bg-primary text-white shadow-md ' +
        'hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ' +
        'transition-opacity duration-200',
        // Wishlist button - icon-only, subtle design with heart icon
        // group/wishlist for hover state on children
        wishlist: 'group/wishlist bg-transparent text-gray-500 ' +
        'border border-gray-300 hover:border-primary hover:text-primary ' +
        'focus:outline-none focus:ring-2 focus:ring-primary/50 ' +
        'transition-all duration-300 ease-in-out [&_svg]:size-5',
        // Expand/collapse button - for show more/less sections
        expand: 'bg-button-color text-white font-main-font font-bold text-lg ' +
        'border border-transparent hover:bg-white hover:text-text-color ' +
        'hover:border-button-color hover:shadow-second-btn-shadow ' +
        'focus:bg-button-color focus:text-white focus:outline-none ' +
        'transition-all duration-300 ease-in-out',
      },
      size: {
        default: 'px-5 py-3',
        sm: 'px-4 py-2',
        lg: 'px-9 py-4',
        icon: 'h-10 w-10',
        // Add to Cart button size
        // h-[45px] w-full p-5, max-w-[80%] for card context
        addToCart: 'h-[45px] w-[80%] max-w-[280px] p-5',
        // PromoBlock button size - larger padding to match addToCart feel
        promoBlock: 'px-8 py-4',
        full: 'w-full py-3',
        // Wishlist button size - square icon button
        wishlist: 'h-[45px] w-[45px] p-2.5 rounded',
        // Scroll to top - fixed position with padding for icon
        scrollToTop: 'h-16 w-16 p-2 fixed bottom-[20px] left-[14px] z-50',
        // Expand button size
        expand: 'w-80 py-3 px-4 gap-3',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export type ButtonVariants = VariantProps<typeof buttonVariants>

/**
 * Add to Cart button label — typography + hover behavior for the text inside
 * the AddToCart button. Coupled with the `addToCart` button variant via the
 * `group/addtocart` parent class on the button.
 */
export const addToCartLabelVariants = cva(
  'font-sec-font text-base md:text-lg lg:text-xl ' +
  'group-hover/addtocart:text-text-color transition-colors duration-300'
)
